import React, { useState } from 'react';
import { X, TrendingUp, TrendingDown, Eye, Sparkles, Loader2 } from 'lucide-react';
import { pnlColor } from '../../utils/pnl';

interface Quote {
  price: number;
  change: number;
  change_percent: number;
  volume?: number;
  high?: number;
  low?: number;
}

interface WatchlistProps {
  symbols: string[];
  quotes: { [key: string]: Quote };
  onAddSymbol: (symbol: string) => void;
  onRemoveSymbol: (symbol: string) => void;
  onSelectSymbol: (symbol: string) => void;
  selectedSymbol: string | null;
  isAdding: boolean;
}

const Watchlist: React.FC<WatchlistProps> = ({
  symbols,
  quotes,
  onAddSymbol,
  onRemoveSymbol,
  onSelectSymbol,
  selectedSymbol,
  isAdding
}) => {
  const [newSymbol, setNewSymbol] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newSymbol.trim()) {
      onAddSymbol(newSymbol.toUpperCase().trim());
      setNewSymbol('');
    }
  };

  // Calculate total portfolio change
  const totalChange = Object.values(quotes).reduce((sum, q) => sum + (q?.change_percent || 0), 0);
  const avgChange = symbols.length > 0 ? totalChange / symbols.length : 0;

  return (
    <div className="flex flex-col h-full bg-gradient-to-b from-slate-800/30 to-slate-900/50">
      {/* Header */}
      <div className="p-3 border-b border-slate-700/50 bg-gradient-to-r from-slate-800/50 to-slate-900/50">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-green-500/20 to-cyan-500/20 flex items-center justify-center border border-green-500/30">
              <Eye className="w-4 h-4 text-green-400" />
            </div>
            <h2 className="text-sm font-bold bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent">Watchlist</h2>
          </div>
          <span className="text-10 text-slate-400 px-2 py-0.5 bg-slate-800/80 rounded-full border border-slate-700/50">
            {symbols.length} assets
          </span>
        </div>
        {/* Average Change */}
        <div className={`flex items-center gap-1.5 text-11 ${pnlColor(avgChange)}`}>
          {avgChange >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          <span>Avg: {avgChange >= 0 ? '+' : ''}{avgChange.toFixed(2)}%</span>
        </div>
      </div>

      {/* Add Symbol Input - Always Visible */}
      <div className="p-2 border-b border-slate-700/30">
        <form onSubmit={handleSubmit} className="flex gap-1">
          <input
            type="text"
            value={newSymbol}
            onChange={(e) => setNewSymbol(e.target.value.toUpperCase())}
            placeholder="Add ticker"
            maxLength={5}
            className="w-full min-w-0 px-2.5 py-1.5 rounded-lg bg-slate-800/80 border border-slate-600/50 text-white placeholder-slate-500 text-xs focus:outline-none focus:ring-1 focus:ring-green-500/50 focus:border-green-500/50 transition-all"
          />
          <button
            type="submit"
            disabled={isAdding || !newSymbol.trim()}
            className="shrink-0 w-8 h-8 rounded-lg bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500 text-white flex items-center justify-center transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-green-500/20"
          >
            {isAdding ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            )}
          </button>
        </form>
      </div>

      {/* Watchlist Items */}
      <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
        {symbols.map((symbol, idx) => {
          const quote = quotes[symbol];
          const isPositive = quote ? quote.change >= 0 : true;
          const isSelected = selectedSymbol === symbol;

          return (
            <div
              key={symbol}
              onClick={() => onSelectSymbol(symbol)}
              className={`relative group p-2.5 rounded-lg cursor-pointer transition-all duration-200 border overflow-hidden ${
                isSelected
                  ? 'bg-gradient-to-r from-green-500/15 to-cyan-500/10 border-green-500/40'
                  : 'bg-gradient-to-r from-slate-800/60 to-slate-800/40 border-slate-700/40 hover:from-slate-700/60 hover:to-slate-800/60 hover:border-slate-600/50'
              }`}
            >
              {/* Gradient Accent */}
              <div className={`absolute left-0 top-0 bottom-0 w-0.5 ${
                isPositive ? 'bg-gradient-to-b from-green-400 to-green-600' : 'bg-gradient-to-b from-red-400 to-red-600'
              }`} />

              {/* Remove Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveSymbol(symbol);
                }}
                className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 p-1 rounded-md bg-slate-700/80 hover:bg-red-500/30 hover:text-red-400 transition-all z-10"
              >
                <X className="w-2.5 h-2.5" />
              </button>

              <div className="flex items-center justify-between pl-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <h3 className="font-bold text-white text-xs">{symbol}</h3>
                    {idx === 0 && (
                      <Sparkles className="w-3 h-3 text-yellow-400" />
                    )}
                  </div>
                  {quote ? (
                    <p className="text-sm font-bold text-white mt-0.5">
                      ${quote.price.toFixed(2)}
                    </p>
                  ) : (
                    <div className="flex items-center gap-1 mt-0.5">
                      <div className="w-12 h-4 bg-slate-700/50 rounded animate-pulse" />
                    </div>
                  )}
                </div>

                {quote && (
                  <div className={`flex flex-col items-end`}>
                    <div className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-11 font-semibold tabular-nums ${
                      isPositive 
                        ? 'bg-gradient-to-r from-green-500/20 to-green-600/10 text-green-400 border border-green-500/20' 
                        : 'bg-gradient-to-r from-red-500/20 to-red-600/10 text-red-400 border border-red-500/20'
                    }`}>
                      {isPositive ? (
                        <TrendingUp className="w-2.5 h-2.5" />
                      ) : (
                        <TrendingDown className="w-2.5 h-2.5" />
                      )}
                      <span>{isPositive ? '+' : ''}{quote.change_percent.toFixed(2)}%</span>
                    </div>
                    <span className={`text-10 mt-0.5 tabular-nums ${isPositive ? 'text-green-400/70' : 'text-red-400/70'}`}>
                      {isPositive ? '+' : ''}${Math.abs(quote.change).toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {symbols.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 text-slate-500">
            <Eye className="w-8 h-8 mb-2 opacity-30" />
            <p className="text-xs">No assets in watchlist</p>
            <p className="text-10 mt-1 opacity-60">Add a ticker above</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Watchlist;
